import { GlobalOutlined } from "@ant-design/icons";
import { getLocale, t, type AppLocale } from "@airange/shared/i18n-platform-locale";
import { App, Select, Space, Typography } from "antd";
import { useMemo, useState } from "react";

export type LocaleOption = {
  value: AppLocale;
};

export type LocaleControllerLike = {
  localeOptions: LocaleOption[];
  switchLocale: (locale: string) => AppLocale;
};

export type LocaleSwitchProps = {
  controller: LocaleControllerLike;
  getLocaleLabel: (locale: AppLocale) => string;
  width?: number;
};

type LocaleSwitchShellConfig = Omit<LocaleSwitchProps, "width"> & {
  width?: number;
};

export function LocaleSwitch({ controller, getLocaleLabel, width = 124 }: LocaleSwitchProps) {
  const { message } = App.useApp();
  const [locale, setLocale] = useState(() => getLocale());
  const options = useMemo(
    () =>
      controller.localeOptions.map((item) => ({
        value: item.value,
        label: getLocaleLabel(item.value),
      })),
    [controller, getLocaleLabel],
  );

  return (
    <Space size={8}>
      <GlobalOutlined />
      <Typography.Text>{t("platform.common.locale")}</Typography.Text>
      <Select
        size="small"
        value={locale}
        options={options}
        style={{ width }}
        onChange={(value: string) => {
          const nextLocale = controller.switchLocale(value);
          setLocale(nextLocale);
          message.success(t("platform.common.languageChanged", { locale: getLocaleLabel(nextLocale) }, nextLocale));
          window.location.reload();
        }}
      />
    </Space>
  );
}

export function createLocaleSwitchShell(config: LocaleSwitchShellConfig) {
  function LocaleSwitchShell() {
    return (
      <LocaleSwitch
        controller={config.controller}
        getLocaleLabel={config.getLocaleLabel}
        {...(config.width === undefined ? {} : { width: config.width })}
      />
    );
  }
  return LocaleSwitchShell;
}
